import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import classnames from 'classnames'
import './SiteLogo.scss'

const SiteLogo = ({
  width,
  height,
  style = {},
  className = '',
  autoPlayIntro = false,
  fillIndex: _fillIndex,
  name: _name,
}) => {
  const strokeRefs = useRef([])
  const introPlayedRef = useRef(false)
  const [ready, setReady] = useState(false)
  const [drawPhase, setDrawPhase] = useState('idle')

  useLayoutEffect(() => {
    strokeRefs.current.forEach((el) => {
      if (!el || typeof el.getTotalLength !== 'function') return
      el.style.setProperty('--len', String(el.getTotalLength()))
    })
    setReady(true)
  }, [])

  const setStrokeRef = (index) => (el) => {
    strokeRefs.current[index] = el
  }

  const playDraw = useCallback(() => {
    if (!ready) return
    setDrawPhase('reset')
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setDrawPhase('play')
      })
    })
  }, [ready])

  useEffect(() => {
    if (!autoPlayIntro || !ready || introPlayedRef.current) return
    introPlayedRef.current = true
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return
    }
    playDraw()
  }, [autoPlayIntro, ready, playDraw])

  return (
    <span
      className={classnames('site-logo', className, {
        'site-logo--ready': ready,
        'site-logo--draw-reset': drawPhase === 'reset',
        'site-logo--draw-play': drawPhase === 'play',
      })}
    >
      <svg
        width={width}
        height={height}
        style={style}
        className="site-logo__svg"
        data-name="site-logo"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 116.91 34.57"
        focusable="false"
      >
        <title>Home</title>
        <g className="site-logo__g" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.98">
          <path
            ref={setStrokeRef(0)}
            className="site-logo__stroke"
            d="M26.39,2.95c-2.26,8.66-3.95,25.04-3.95,25.04"
          />
          <path
            ref={setStrokeRef(1)}
            className="site-logo__stroke"
            d="M24.88,29.49c5.84-16.38-13.68-15.12-20.52-8.28-8.59,8.59.92,12.38,9.74,12.38,13.36,0,12.38-1.93,12.38-1.93"
          />
          <path
            ref={setStrokeRef(2)}
            className="site-logo__stroke"
            d="M26.39,20.83c9.41-8.47,9.65-2.21,6.78,1.13s-9.24,8.61-2.26,6.59c4.14-1.2,5.84-1.32,5.84-1.32"
          />
          <line
            ref={setStrokeRef(3)}
            className="site-logo__stroke"
            x1="44.03"
            y1="15.35"
            x2="36.69"
            y2="17.6"
          />
          <line
            ref={setStrokeRef(4)}
            className="site-logo__stroke"
            x1="41.02"
            y1="20.71"
            x2="36.69"
            y2="21.93"
          />
          <path
            ref={setStrokeRef(5)}
            className="site-logo__stroke"
            d="M37.73,17.25s-.99,7.76-1.27,9.39,7.13-.92,7.13-.92"
          />
          <path
            ref={setStrokeRef(6)}
            className="site-logo__stroke"
            d="M44.08,18.33v10.54l4.33-6.59s-.75,5.84,2.82,5.84c5.08,0,4.42-24.19-9.69-17.51,0,0-2.66,1.27.07,1.55"
          />
          <path
            ref={setStrokeRef(7)}
            className="site-logo__stroke"
            d="M56.13,10.52c2.82,3.39,3.39,6.35,2.26,9.46"
          />
          <path
            ref={setStrokeRef(8)}
            className="site-logo__stroke"
            d="M53.87,7.42c11.58-5.08,21.18-4.38,21.18-1.98,0,3.81-19.48,17.79-19.48,23.58s16.8,4.94,24.28-.56"
          />
          <path
            ref={setStrokeRef(9)}
            className="site-logo__stroke"
            d="M69.82,18.15c-5.65,5.08-4.66,6.64-3.53,7.62s6.49-1.69,6.49-4.09-.85-3.88-3.53-4.87"
          />
          <path
            ref={setStrokeRef(10)}
            className="site-logo__stroke"
            d="M74.2,13.56c2.12,4.38.56,12.35.56,12.35,0,0,5.65-.42,5.65-4.73s-4.21-3.36-5.13-1.11"
          />
          <path
            ref={setStrokeRef(11)}
            className="site-logo__stroke"
            d="M83.56,22.75c3.62-2.57,3.29-4.03,3.27-4.96-.04-2.14-3.97-2.63-3.97,1.28s-.41,10.62,5.84,4.73"
          />
          <path
            ref={setStrokeRef(12)}
            className="site-logo__stroke"
            d="M89.59,25.77c.28-4.52-.07-10.24-.07-10.24"
          />
          <path
            ref={setStrokeRef(13)}
            className="site-logo__stroke"
            d="M89.66,16.24s4.94-3.04,4.94-.21-2.47,2.96-2.47,5.79,2.14,2.21,3.34.73"
          />
          <path
            ref={setStrokeRef(14)}
            className="site-logo__stroke"
            d="M102.36,12.92c-1.62,5.29-1.62,13.13-1.62,13.13"
          />
          <path
            ref={setStrokeRef(15)}
            className="site-logo__stroke"
            d="M108.01.99s-4.38,1.76-4.38,5.79,12.28,7.76,12.28,15.88-5.44,10.73-9.53,10.73-10.87-2.26-10.87-9.25,8.4-7.2,10.02-6.42"
          />
        </g>
      </svg>
    </span>
  )
}

export default SiteLogo
